import { useEffect, useRef } from 'react';
import cn from 'classnames';

import { IKvinnehistorieTimelinePage_NavItem } from 'source/NFR.types';

interface ScrubberTrackProps {
  items: IKvinnehistorieTimelinePage_NavItem[];
  className: string;
  label: string;
  activeIndex?: number;
  onNavigate?: (anchorId: string) => void;
}

const ScrubberTrack: React.FC<ScrubberTrackProps> = ({ items, className, label, activeIndex = -1, onNavigate }) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });

  // When the active tick changes (scroll-spy or navigation), keep it visible in the rail.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || activeIndex < 0 || drag.current.active) return;
    const tick = track.children[activeIndex] as HTMLElement | undefined;

    if (!tick) return;
    const left = tick.offsetLeft;
    const right = left + tick.offsetWidth;

    if (left < track.scrollLeft || right > track.scrollLeft + track.clientWidth) {
      track.scrollTo({ left: left - track.clientWidth / 2 + tick.offsetWidth / 2 });
    }
  }, [activeIndex]);

  const onPointerDown = (e: React.PointerEvent<HTMLUListElement>) => {
    const el = trackRef.current;
    if (!el) return;

    drag.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: 0 };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
    const el = trackRef.current;
    if (!el || !drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
    // Capture only once it is clearly a drag: capturing on pointerdown would retarget
    // the eventual click to the list, so plain clicks on the tick links would be lost.
    if (drag.current.moved > 5 && !el.hasPointerCapture(e.pointerId)) {
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = drag.current.startScroll - dx;
  };

  const endDrag = (e: React.PointerEvent<HTMLUListElement>) => {
    drag.current.active = false;
    const el = trackRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
  };

  // Ignore the click that finishes a drag, so scrubbing doesn't navigate.
  const onClickCapture = (e: React.MouseEvent<HTMLUListElement>) => {
    if (drag.current.moved > 5) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onTickClick = (anchorId: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate(anchorId);
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <ul
      ref={trackRef}
      className={cn('kvinnehistorie-scrubber--track', className)}
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
      onClickCapture={onClickCapture}
    >
      {items.map((item, index) => (
        <li className="kvinnehistorie-scrubber--tick" key={item.anchorId}>
          <a
            className={cn('kvinnehistorie-scrubber--link', { 'is-active': index === activeIndex })}
            href={`#${item.anchorId}`}
            onClick={onTickClick(item.anchorId)}
            aria-current={index === activeIndex ? 'true' : undefined}
          >
            <span className="kvinnehistorie-scrubber--label">{item.title}</span>
            <span className="kvinnehistorie-scrubber--rail" aria-hidden="true">
              <span className="kvinnehistorie-scrubber--dot" />
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
};

ScrubberTrack.displayName = 'ScrubberTrack';

export default ScrubberTrack;
